import { supabase } from '@/integrations/supabase/client';
import type { TerminalLocationKind } from '@/utils/ticketTerminalLifecycle';

/**
 * OCT20 WS-ROLL (FD-04) — "Delete Entire Rollover".
 *
 * A day of a multi-day rollover is never deleted alone (the ordinary delete answers ROLLOVER_CHAIN_MEMBER — deleting
 * one day stranded the later days as live orphans). An Owner or Manager may instead delete the WHOLE repair as ONE
 * server act: `delete_rollover_chain_if_quiescent` derives every day itself under ordered locks, refuses while any
 * technician is working / paused / owed a review, while a vehicle in a bay has no destination, while finalized
 * commercial evidence exists, or when the repair is already complete — and otherwise marks every day Deleted
 * (lineage, measured time, notes and evidence kept), with one audit row per day.
 *
 * The browser never names the days: `rollover_chain_delete_preview` lists them for the warning, and the act derives
 * them again under its own locks. A deleted rollover cannot be restored from the app (pilot limitation).
 */

type Rpc = (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
// Receiver-preserving (see deleteTicketFamily.ts): never detach `supabase.rpc`.
const rpc: Rpc = (fn, args) => (supabase.rpc as unknown as Rpc)(fn, args);

export type ChainDeleteOutcome =
  | 'CHAIN_DELETED' | 'ALREADY_APPLIED' | 'PREVIEW'
  | 'TICKET_NOT_AVAILABLE' | 'CHAIN_DELETE_NOT_AUTHORIZED' | 'REASON_REQUIRED' | 'NOT_A_ROLLOVER_CHAIN'
  | 'FAMILY_CHANGED_RETRY' | 'FAMILY_LINEAGE_INVALID' | 'CHAIN_COMPLETED' | 'COMMERCIALLY_FINALIZED'
  | 'PROTECTED_OPERATION_ACTIVE' | 'ACTIVE_WORK' | 'PAUSED_WORK' | 'REVIEW_REQUIRED' | 'TIMER_INTEGRITY_BLOCK'
  | 'LOCATION_REQUIRED' | 'SUBMIT_FAILED';

const KNOWN: ChainDeleteOutcome[] = [
  'CHAIN_DELETED', 'ALREADY_APPLIED', 'PREVIEW', 'TICKET_NOT_AVAILABLE', 'CHAIN_DELETE_NOT_AUTHORIZED',
  'REASON_REQUIRED', 'NOT_A_ROLLOVER_CHAIN', 'FAMILY_CHANGED_RETRY', 'FAMILY_LINEAGE_INVALID', 'CHAIN_COMPLETED',
  'COMMERCIALLY_FINALIZED', 'PROTECTED_OPERATION_ACTIVE', 'ACTIVE_WORK', 'PAUSED_WORK', 'REVIEW_REQUIRED',
  'TIMER_INTEGRITY_BLOCK', 'LOCATION_REQUIRED', 'SUBMIT_FAILED',
];

export interface ChainSegment {
  ticketId: string;
  ticketNumber: number | null;
  scheduledDate: string | null;
  status: string;
  isHead: boolean;
  recordedMinutes: number;
  inBay: boolean;
}

export interface ChainBlocker {
  outcome: ChainDeleteOutcome;
  blockedTicketId: string | null;
  note: string | null;
}

export interface ChainDeletePreview {
  outcome: ChainDeleteOutcome;
  headTicketId: string | null;
  segments: ChainSegment[];
  mayDelete: boolean;
  blocker: ChainBlocker | null;
}

export interface ChainDeleteResult {
  outcome: ChainDeleteOutcome;
  deleted: Array<{ ticketId: string; scheduledDate: string | null; statusBefore: string }>;
  lanes: Array<{ technicianId: string; scheduledDate: string }>;
  blocker: ChainBlocker | null;
  note: string | null;
}

const outcomeOf = (v: unknown): ChainDeleteOutcome =>
  KNOWN.includes(v as ChainDeleteOutcome) ? (v as ChainDeleteOutcome) : 'SUBMIT_FAILED'; // unknown fails closed

function blockerOf(r: Record<string, unknown> | null | undefined): ChainBlocker | null {
  if (!r || !r.outcome) return null;
  return { outcome: outcomeOf(r.outcome), blockedTicketId: (r.blocked_ticket_id as string) ?? null, note: (r.note as string) ?? null };
}

export async function previewRolloverChainDelete(ticketId: string): Promise<ChainDeletePreview> {
  const { data, error } = await rpc('rollover_chain_delete_preview', { p_ticket_id: ticketId });
  if (error) return { outcome: 'SUBMIT_FAILED', headTicketId: null, segments: [], mayDelete: false, blocker: null };
  const r = (data ?? {}) as Record<string, unknown>;
  const outcome = outcomeOf(r.outcome);
  const segments = Array.isArray(r.segments) ? (r.segments as Array<Record<string, unknown>>).map((s) => ({
    ticketId: String(s.ticket_id),
    ticketNumber: s.ticket_number == null ? null : Number(s.ticket_number),
    scheduledDate: (s.scheduled_date as string) ?? null,
    status: String(s.status ?? ''),
    isHead: s.is_head === true,
    recordedMinutes: Number(s.recorded_minutes ?? 0),
    inBay: s.in_bay === true,
  })) : [];
  return {
    outcome,
    headTicketId: (r.head_ticket_id as string) ?? null,
    segments,
    mayDelete: outcome === 'PREVIEW' && r.may_delete === true,
    blocker: outcome === 'PREVIEW' ? blockerOf(r.blocker as Record<string, unknown> | null) : blockerOf(r),
  };
}

export async function deleteRolloverChain(
  ticketId: string,
  reason: string,
  locationKind: TerminalLocationKind | null = null,
  parkingSpotId: string | null = null,
): Promise<ChainDeleteResult> {
  const { data, error } = await rpc('delete_rollover_chain_if_quiescent', {
    p_ticket_id: ticketId,
    p_reason: reason,
    p_location_kind: locationKind,
    p_parking_spot_id: locationKind === 'PARKING_SPOT' ? (parkingSpotId ?? null) : null,
  });
  if (error) return { outcome: 'SUBMIT_FAILED', deleted: [], lanes: [], blocker: null, note: error.message };
  const r = (data ?? {}) as Record<string, unknown>;
  const outcome = outcomeOf(r.outcome);
  return {
    outcome,
    deleted: Array.isArray(r.deleted) ? (r.deleted as Array<Record<string, unknown>>).map((d) => ({
      ticketId: String(d.ticket_id), scheduledDate: (d.scheduled_date as string) ?? null, statusBefore: String(d.status_before ?? ''),
    })) : [],
    lanes: Array.isArray(r.lanes) ? (r.lanes as Array<Record<string, unknown>>).map((l) => ({
      technicianId: String(l.technician_id), scheduledDate: String(l.scheduled_date),
    })) : [],
    blocker: outcome === 'CHAIN_DELETED' || outcome === 'ALREADY_APPLIED' ? null : blockerOf(r),
    note: (r.note as string) ?? null,
  };
}

export const isChainDeleteSettled = (o: ChainDeleteOutcome) => o === 'CHAIN_DELETED' || o === 'ALREADY_APPLIED';

/** Why the whole-repair delete was refused, in the operator's language. Nothing was written on any refusal. */
export function describeChainDeleteRefusal(b: ChainBlocker | null, segments: ChainSegment[] = []): string {
  if (!b) return '';
  const seg = segments.find((s) => s.ticketId === b.blockedTicketId);
  const which = seg
    ? ` (${seg.ticketNumber != null ? `#${seg.ticketNumber}` : seg.ticketId}${seg.scheduledDate ? `, ${seg.scheduledDate}` : ''})`
    : b.blockedTicketId ? ` (${b.blockedTicketId})` : '';
  switch (b.outcome) {
    case 'ACTIVE_WORK': return `A technician is still working on a day of this repair${which}. They need to stop first.`;
    case 'PAUSED_WORK': return `A technician has paused work on a day of this repair${which} and intends to come back.`;
    case 'REVIEW_REQUIRED': return `Measured time on a day of this repair${which} still needs allocating to jobs.`;
    case 'TIMER_INTEGRITY_BLOCK': return `A day of this repair${which} has timer evidence that needs a person to look at it.`;
    case 'PROTECTED_OPERATION_ACTIVE': return `An unattended operation is still running on this vehicle${which}. End it first.`;
    case 'LOCATION_REQUIRED': return `The vehicle is in a bay${which}. Say where it goes.`;
    case 'CHAIN_COMPLETED': return 'The last day of this repair is completed. A completed repair is corrected, not deleted.';
    case 'COMMERCIALLY_FINALIZED':
      return 'This repair has a finalized or paid invoice, a payment, or a commercial closeout. That record cannot be bypassed by a deletion — void or correct it through invoicing first.';
    case 'CHAIN_DELETE_NOT_AUTHORIZED': return 'Only an Owner or a Manager can delete an entire multi-day repair.';
    case 'REASON_REQUIRED': return 'Say why the whole repair is being deleted.';
    case 'NOT_A_ROLLOVER_CHAIN': return 'This ticket is not part of a multi-day rollover. Use the ordinary Delete.';
    case 'FAMILY_CHANGED_RETRY': return 'The days of this repair changed while this was starting. Try again.';
    case 'FAMILY_LINEAGE_INVALID': return b.note ?? 'This repair is linked to another shop. Someone needs to sort that out first.';
    case 'TICKET_NOT_AVAILABLE': return 'This ticket is not available.';
    case 'SUBMIT_FAILED': return 'The result could not be confirmed. Try again.';
    default: return b.note ?? '';
  }
}
